
import React, { useState, useEffect } from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddonService from "services/addon-service";
import DataTable from "examples/Tables/DataTable";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import LiveDemoRestriction from "layouts/components/Upgrade/liveDemo"; // [NEW]
import { usePermissions } from "context"; // [NEW]
import UpgradePopup from "layouts/components/Upgrade"; // [NEW]

const Pack = ({ onClose, entitlements }) => {

    const [addons, setAddons] = useState([]);
    const [loading, setLoading] = useState(true);

    // State for Confirmation Dialog
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [selectedPack, setSelectedPack] = useState(null);

    const [message, setMessage] = useState(null); // Message to show after purchase
    const [messageType, setMessageType] = useState("success"); // "success" or "error"

    const [showDemoPopup, setShowDemoPopup] = useState(false); // [NEW] Demo Popup State
    const [showRBACPopup, setShowRBACPopup] = useState(false); // [NEW] RBAC Popup State

    const { hasPermission } = usePermissions(); // [NEW]
    const canManageBilling = hasPermission("billing:manage"); // [NEW]

    useEffect(() => {
        const fetchAddons = async () => {
            try {
                const response = await AddonService.fetchAddons();
                // Sort by code (numeric)
                const sortedAddons = response.sort((a, b) => (a.code || 0) - (b.code || 0));
                setAddons(sortedAddons);
            } catch (error) {
                console.error("Error fetching addons:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchAddons();
    }, []);

    const handleSelectPack = (pack) => {
        // [NEW] RBAC Restriction
        if (!canManageBilling) {
            setShowRBACPopup(true);
            return;
        }

        // [NEW] Live Demo Restriction
        const isDemo = sessionStorage.getItem('demo_role_cache') === "demo_viewer";
        if (isDemo) {
            setShowDemoPopup(true);
            return;
        }
        setSelectedPack(pack);
        setOpenConfirmDialog(true);
    };

    const confirmPurchase = async () => {
        setOpenConfirmDialog(false);

        if (!selectedPack) return;

        try {
            const response = await AddonService.purchaseAddon(selectedPack._id);

            if (response && response.url) {
                window.location.href = response.url;
            } else {
                throw new Error("Invalid response from server.");
            }

        } catch (err) {
            console.error("Purchase failed:", err);
            setMessage("Purchase failed. Please try again.");
            setMessageType("error");
        }
    };

    if (loading) {
        return <MDTypography variant="body2" color="textSecondary" textAlign="center" py={3}>Loading packs...</MDTypography>;
    }

    // --- PREPARE DATA FOR DATATABLE ---

    const columns = [
        {
            Header: (
                <MDBox py={1} px={2} pl={3}>
                    <MDTypography variant="caption" sx={{ fontWeight: 800 }}>
                        PACK NAME
                    </MDTypography>
                </MDBox>
            ),
            accessor: "name",
            width: "25%",
            align: "left"
        },
        {
            Header: (
                <MDBox py={1} px={2} pl={3}>
                    <MDTypography variant="caption" sx={{ fontWeight: 800 }}>
                        DESCRIPTION
                    </MDTypography>
                </MDBox>
            ),
            accessor: "description",
            width: "35%",
            align: "left"
        },
        {
            Header: (
                <MDBox py={1} px={2} pl={3}>
                    <MDTypography variant="caption" sx={{ fontWeight: 800 }}>
                        PACK TYPE
                    </MDTypography>
                </MDBox>
            ),
            accessor: "type",
            width: "15%",
            align: "left"
        },
        {
            Header: (
                <MDBox py={1} px={2} pl={3}>
                    <MDTypography variant="caption" sx={{ fontWeight: 800 }}>
                        VALUE
                    </MDTypography>
                </MDBox>
            ),
            accessor: "value",
            width: "10%",
            align: "left"
        },
        {
            Header: (
                <MDBox py={1} px={2} pl={3}>
                    <MDTypography variant="caption" sx={{ fontWeight: 800 }}>
                        COST
                    </MDTypography>
                </MDBox>
            ),
            accessor: "cost",
            width: "10%",
            align: "left"
        },
        {
            Header: (
                <MDBox py={1} px={2} pl={3}>
                    <MDTypography variant="caption" sx={{ fontWeight: 800 }}>
                        ACTION
                    </MDTypography>
                </MDBox>
            ),
            accessor: "action",
            width: "15%",
            align: "center"
        },
    ];

    const filteredAddons = addons.filter(addon => {
        if (addon.type.toLowerCase() === "snapshot") {
            // Only show snapshot addons if the user's plan supports the "Snapshots" feature
            return entitlements?.features?.includes("Snapshot");
        }
        return true;
    });

    const rows = filteredAddons.map((pack) => ({
        name: (
            <MDTypography variant="subtitle2" fontWeight="bold">
                {pack.name}
            </MDTypography>
        ),
        description: (
            <MDTypography variant="caption" color="text">
                {pack.description}
            </MDTypography>
        ),
        type: (
            <MDTypography variant="caption" fontWeight="medium" textTransform="capitalize">
                {pack.type}
            </MDTypography>
        ),
        value: (
            <MDTypography variant="subtitle2" fontWeight="bold">
                {pack.value}
            </MDTypography>
        ),
        cost: (
            <MDTypography variant="subtitle2" fontWeight="bold">
                £{pack.price}
            </MDTypography>
        ),
        action: (
            <MDButton
                variant="contained"
                color="accent"
                size="small"
                onClick={() => handleSelectPack(pack)}
            >
                Select
            </MDButton>
        ),
    }));

    return (
        <Card sx={{ position: "relative", minHeight: "80vh" }}>
            <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h3" fontWeight="bold" textAlign="center" flexGrow={1}>
                    Add-on Store
                </MDTypography>
                {onClose && (
                    <IconButton onClick={onClose} sx={{ color: "gray" }}>
                        <CloseIcon />
                    </IconButton>
                )}
            </MDBox>

            {message && (
                <MDBox textAlign="center" my={2}>
                    <MDTypography
                        variant="body2"
                        color={messageType === "success" ? "success.main" : "error.main"}
                    >
                        {message}
                    </MDTypography>
                </MDBox>
            )}

            <MDBox p={3}>
                <DataTable
                    table={{ columns, rows }}
                    showTotalEntries={false}
                    isSorted={false}
                    noEndBorder={true}
                    entriesPerPage={false}
                />
            </MDBox>

            {/* Confirmation Dialog */}
            <Dialog
                open={openConfirmDialog}
                onClose={() => setOpenConfirmDialog(false)}
                maxWidth="xs"
            >
                <DialogTitle>Confirm Purchase</DialogTitle>
                <DialogContent>
                    <MDTypography variant="body2">
                        Are you sure you want to purchase <strong>{selectedPack?.name}</strong> for{" "}
                        <strong>£{selectedPack?.price}</strong>?
                    </MDTypography>
                    <MDTypography variant="caption" color="text" display="block" mt={1}>
                        This is a one-time payment and permanently increases your limits.
                    </MDTypography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirmDialog(false)} color="error">
                        Cancel
                    </Button>
                    <Button onClick={confirmPurchase} color="primary">
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>


            {/* [NEW] Live Demo Restriction Popup */}
            <LiveDemoRestriction
                open={showDemoPopup}
                onClose={() => setShowDemoPopup(false)}
                featureName="Purchasing Add-on"
            />

            {/* [NEW] RBAC Restriction Popup */}
            <UpgradePopup
                type="rbac"
                open={showRBACPopup}
                onClose={() => setShowRBACPopup(false)}
                roleRequired="Owner"
                message="Only Workspace Owners can purchase add-ons and manage billing settings."
            />
        </Card >
    );
};

export default Pack;
